<template>
  <div
    @click="$emit('click', $event)"
    class="app-payment-card"
    :class="[brand, { vertical, active }]"
  >
    <i class="chip"></i>

    <template v-if="brandImage">
      <app-icon
        v-if="['visa', 'american-express'].includes(brand)"
        :src="brandImage"
        class="brand-image"
        contain
        width="60px"
        height="35px"
      />

      <app-image
        v-else
        :src="brandImage"
        class="brand-image"
        contain
        height="35px"
      />
    </template>

    <span v-if="lastNumbers" class="number">•••• {{ lastNumbers }}</span>
    <span v-if="holderName" v-text="holderName" class="holder-name"></span>
    <span v-if="expiry" class="expiry"
      >{{ expiry.month }}/{{ expiry.year }}</span
    >
  </div>
</template>

<script>
import CardValidator from 'card-validator'
import {
  getPaymentCardNumberLastDigits,
  getPaymentCardBrandImage,
  accentsTidy,
  slug,
} from '@/helpers'

export default {
  name: 'AppPaymentCard',

  emits: ['click'],

  data() {
    return {
      paymentCard: undefined,
      keyupEvent: new KeyboardEvent('keyup'),
    }
  },

  props: {
    number: {
      type: [Number, String],
    },

    holderName: {
      type: String,
    },

    expiry: {
      type: [String, Object],
    },

    securityCode: {
      type: [String, Number],
    },

    vertical: {
      type: Boolean,
      default: false,
    },

    active: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    lastNumbers() {
      return getPaymentCardNumberLastDigits({ number: this.number })
    },

    brand() {
      const cardBrand = app.getPaymentCardBrand(this.number)

      return slug(cardBrand, { lower: true })
    },

    brandImage() {
      return this.getPaymentCardImage(this.number)
    },

    holderNameFormatted() {
      return accentsTidy(this.holderName)
        .toUpperCase()
        .replace(/[^a-zA-Z 0-9]/gi, '')
    },

    numberFormatted() {
      return this.number.replace(/[^ 0-9]/gi, '')
    },

    expiryFormatted() {
      return this.expiry.replace(/[^/0-9]/gi, '')
    },

    numberValidator() {
      return this.validateNumber()
    },

    expiryValidator() {
      return this.validateExpiry()
    },
  },

  methods: {
    getPaymentCardNumberLastDigits,

    validate({ field, value }) {
      if (!value) return 'NOT_COMPLETED'

      let validatorMethod = field

      switch (validatorMethod) {
        case 'expiry':
          validatorMethod = 'expirationDate'
          break

        case 'securityCode':
          validatorMethod = 'cvv'
          break
      }

      const validatorResult = CardValidator[validatorMethod](value)

      if (validatorResult.isValid === false) return 'INVALID'

      return 'VALID'
    },

    validateNumber(value = this.numberFormatted) {
      return this.validate({
        field: 'number',
        value,
      })
    },

    validateExpiry(value = this.expiryFormatted) {
      return this.validate({
        field: 'expiry',
        value,
      })
    },

    validateSecurityCode(value = this.securityCode) {
      return this.validate({
        field: 'securityCode',
        value,
      })
    },

    getExpiry(expiry = this.expiry) {
      const validatorResult = CardValidator.expirationDate(expiry)

      return {
        month: parseInt(validatorResult.month),
        year: parseInt(validatorResult.year),
      }
    },

    getMaxSecurityCodeLength(number = this.numberFormatted) {
      const validatorResult = CardValidator.number(number)

      return validatorResult?.card?.code?.size ?? 4
    },

    getPaymentCardImage(number) {
      if (!number) return

      return getPaymentCardBrandImage({ number })
    },
  },
}
</script>

<style lang="scss" scoped>
@use 'sass:color';

@use '@/styles/settings.scss' as *;
@use '@/styles/functions.scss' as *;

.app-payment-card {
  $padding: 20px;
  $backgroundColor: #ddd;
  $textColor: get-text-color($backgroundColor);
  $textColorOpacity: 0.65;
  background-color: $backgroundColor;
  color: rgba($textColor, $textColorOpacity);
  border-radius: 20px;
  padding: $padding;
  position: relative;
  opacity: 0.5;
  transition:
    opacity $defaultTransitionDuration,
    background-color $defaultTransitionDuration,
    color $defaultTransitionDuration;
  background-image: repeating-linear-gradient(
      45deg,
      rgba(255, 255, 255, 0) 1px,
      rgba(255, 255, 255, 0.03) 2px,
      rgba(255, 255, 255, 0.04) 3px,
      rgba(255, 255, 255, 0.05) 4px
    ),
    repeating-linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.05) 1px,
      rgba(255, 255, 255, 0) 2px,
      rgba(255, 255, 255, 0.04) 3px,
      rgba(255, 255, 255, 0.03) 4px
    ),
    repeating-linear-gradient(
      90deg,
      rgba(255, 255, 255, 0) 1px,
      rgba(255, 255, 255, 0.03) 2px,
      rgba(255, 255, 255, 0.04) 3px,
      rgba(255, 255, 255, 0.05) 4px
    ),
    repeating-linear-gradient(
      210deg,
      rgba(255, 255, 255, 0) 1px,
      rgba(255, 255, 255, 0.03) 2px,
      rgba(255, 255, 255, 0.04) 3px,
      rgba(255, 255, 255, 0.05) 4px
    ),
    repeating-radial-gradient(
      circle at 30% 30%,
      rgba(255, 255, 255, 0) 1px,
      rgba(255, 255, 255, 0.03) 2px,
      rgba(255, 255, 255, 0.04) 3px,
      rgba(255, 255, 255, 0.05) 4px
    ),
    repeating-radial-gradient(
      circle at 70% 70%,
      rgba(255, 255, 255, 0) 1px,
      rgba(255, 255, 255, 0.03) 2px,
      rgba(255, 255, 255, 0.04) 3px,
      rgba(255, 255, 255, 0.05) 4px
    ),
    repeating-radial-gradient(
      circle at 90% 20%,
      rgba(255, 255, 255, 0) 1px,
      rgba(255, 255, 255, 0.03) 2px,
      rgba(255, 255, 255, 0.04) 3px,
      rgba(255, 255, 255, 0.05) 4px
    ),
    repeating-radial-gradient(
      circle at 15% 80%,
      rgba(255, 255, 255, 0) 1px,
      rgba(255, 255, 255, 0.03) 2px,
      rgba(255, 255, 255, 0.04) 3px,
      rgba(255, 255, 255, 0.05) 4px
    ),
    linear-gradient(
      -25deg,
      rgba(255, 255, 255, 0) 50%,
      rgba(255, 255, 255, 0.2) 70%,
      rgba(255, 255, 255, 0) 90%
    );

  &.active {
    opacity: 1;
  }

  > .expiry {
    font-size: 75%;
  }

  > .holder-name {
    text-transform: uppercase;
    font-size: 16px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: horizontal;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &.vertical {
    $width: 165px;
    $height: 230px;
    min-width: $width;
    max-width: $width;
    min-height: $height;
    max-height: $height;
    display: flex;
    flex-direction: column;

    > .brand-image {
      position: absolute !important;
      bottom: $padding;
      right: $padding;
      max-width: 35%;
      color: #fff;
    }

    > * {
      margin-top: 0.5rem;

      &:first-child {
        margin-top: 0;
      }
    }

    > .chip {
      $width: 50px;
      $height: 35px;
      $color: #fcd462;
      min-width: $width;
      max-width: $width;
      min-height: $height;
      max-height: $height;
      border-radius: 5px;
      background-color: $color;
      margin-left: auto;
      margin-bottom: 1rem;
      position: relative;

      &::before {
        content: '';
        display: block;
        width: 70%;
        height: 60%;
        border-top-right-radius: 5px;
        border-bottom-right-radius: 5px;
        background-color: color.adjust($color, $lightness: -10%);
        position: absolute;
        top: 20%;
      }
    }
  }

  &.visa {
    $backgroundColor: #191278;
    $textColor: get-text-color($backgroundColor);
    background-color: $backgroundColor;
    color: rgba($textColor, $textColorOpacity);

    > .brand-image {
      .mask {
        background-color: #fff;
      }
    }
  }

  &.mastercard {
    $backgroundColor: #0061a8;
    $textColor: get-text-color($backgroundColor);
    background-color: $backgroundColor;
    color: rgba($textColor, $textColorOpacity);
  }

  &.elo {
    $backgroundColor: #6f6969;
    $textColor: get-text-color($backgroundColor);
    background-color: $backgroundColor;
    color: rgba($textColor, $textColorOpacity);
  }

  &.diners-club {
    $backgroundColor: #999;
    $textColor: get-text-color($backgroundColor);
    background-color: $backgroundColor;
    color: rgba($textColor, $textColorOpacity);
  }

  &.american-express {
    $backgroundColor: #108168;
    $textColor: get-text-color($backgroundColor);
    background-color: $backgroundColor;
    color: rgba($textColor, $textColorOpacity);
  }
}
</style>
